import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import VehicleSelector from "../components/VehicleSelector";
import { Reveal } from "../components/Reveal";
import { useCart } from "../context/CartContext";
import { Gauge, CheckCircle2, Plus, Sparkles } from "lucide-react";

const CAT_LABEL = { rims: "Rims", tires: "Tires", suspension: "Suspension" };
const money = (n) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function BuildBundle({ rim, tire, kit, label }) {
  const { addItem } = useCart();
  const RIM_QTY = 4, TIRE_QTY = 4, KIT_QTY = 1;
  const DISCOUNT = 0.1;

  // Auto-suggest a tire size that matches the chosen wheel diameter (+ lift height).
  const wheelDia = rim.filters?.diameter;
  const liftNum = parseFloat(String(kit?.specs?.["Lift Height"] ?? kit?.sub_category ?? "")) || 0;
  const target = liftNum >= 6 ? 37 : liftNum >= 2.5 ? 35 : 33;
  const suggestedSize = (() => {
    const opts = tire.size_options || [];
    if (!opts.length) return null;
    const pool = opts.filter((o) => o.size.endsWith(`R${wheelDia}`));
    const list = pool.length ? pool : opts;
    const order = target === 37 ? [37, 35, 33] : target === 35 ? [35, 33, 37] : [33, 35, 37];
    for (const d of order) {
      const hit = list.find((o) => o.size.startsWith(`${d}x12.50`)) || list.find((o) => o.size.startsWith(`${d}x13.50`));
      if (hit) return hit;
    }
    return list[0];
  })();
  const tirePrice = suggestedSize ? suggestedSize.price : tire.price;
  const tireName = suggestedSize ? `${tire.name} — ${suggestedSize.size}` : tire.name;

  const regular = rim.price * RIM_QTY + tirePrice * TIRE_QTY + kit.price * KIT_QTY;
  const bundle = regular * (1 - DISCOUNT);
  const save = regular - bundle;
  const parts = [
    { p: rim, qty: RIM_QTY, tag: "Wheels ×4", label: rim.name },
    { p: { ...tire, name: tireName, price: tirePrice }, qty: TIRE_QTY, tag: suggestedSize ? `Tires ×4 · ${suggestedSize.size}` : "Tires ×4", label: tire.name },
    { p: kit, qty: KIT_QTY, tag: "Suspension", label: kit.name },
  ];

  const addBuild = () => {
    parts.forEach(({ p, qty }) =>
      addItem({ ...p, name: `${p.name} — Build Bundle`, price: Math.round(p.price * (1 - DISCOUNT) * 100) / 100 }, qty)
    );
  };

  return (
    <Reveal>
      <div
        data-testid="complete-the-build"
        className="relative overflow-hidden border border-[#ff6a00]/40 bg-gradient-to-br from-[#1a1206] via-[#120c04] to-[#0a0a0a] p-6 md:p-8 mb-16"
      >
        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#ff6a00]/10 blur-3xl pointer-events-none" />
        <div className="flex items-center gap-2 mb-1">
          <Sparkles size={16} className="text-[#ffcf00]" />
          <span className="text-xs uppercase tracking-[0.3em] bttf-text font-semibold">Complete the Build</span>
        </div>
        <h2 className="font-display text-3xl md:text-4xl uppercase leading-none">A matched setup for your {label}</h2>
        <p className="text-zinc-400 text-sm mt-2 max-w-xl">A pro-picked wheel, tire and suspension combo that fits and works together — bundle all three and save {Math.round(DISCOUNT * 100)}%.</p>
        {suggestedSize && (
          <p className="text-xs text-[#ffcf00] mt-1" data-testid="bundle-suggested-size">
            Tire size auto-matched to your {wheelDia}" wheels: <span className="font-semibold">{suggestedSize.size}</span>
          </p>
        )}

        <div className="grid md:grid-cols-[1fr_auto] gap-6 mt-6 items-center">
          <div className="flex flex-col sm:flex-row items-stretch gap-3">
            {parts.map(({ p, tag, label }, i) => (
              <React.Fragment key={p.id}>
                <Link
                  to={`/product/${p.slug}`}
                  data-testid={`bundle-item-${i}`}
                  className="group flex-1 flex items-center gap-3 border border-[#27272a] bg-[#0d0d0d] p-3 hover:border-[#00f0ff] transition-colors min-w-0"
                >
                  <div className="w-16 h-16 shrink-0 bg-gradient-to-br from-[#161616] to-[#080808] border border-[#27272a]">
                    <img src={p.images?.[0]} alt={label} className="w-full h-full object-contain p-1.5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[#ff6a00]">{tag}</div>
                    <div className="text-sm font-semibold text-white truncate group-hover:text-[#00f0ff] transition-colors">{label}</div>
                    <div className="text-xs text-zinc-500">{p.brand}</div>
                  </div>
                </Link>
                {i < parts.length - 1 && <div className="hidden sm:flex items-center text-zinc-600"><Plus size={18} /></div>}
              </React.Fragment>
            ))}
          </div>

          <div className="flex md:flex-col items-center md:items-end justify-between gap-3 md:gap-2 md:min-w-[190px]">
            <div className="text-right">
              <div className="text-xs text-zinc-500 line-through">{money(regular)}</div>
              <div className="font-display text-3xl bttf-text leading-none" data-testid="bundle-total">{money(bundle)}</div>
              <div className="text-xs text-[#ffcf00] mt-1">You save {money(save)}</div>
            </div>
            <button
              onClick={addBuild}
              data-testid="add-build-to-cart"
              className="bttf-gradient text-black font-semibold uppercase tracking-wider text-sm px-6 py-3 hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              Add Build to Cart
            </button>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function Fitment() {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const year = searchParams.get("year");
  const make = searchParams.get("make");
  const makeName = searchParams.get("makeName") || make;
  const model = searchParams.get("model");
  const modelName = searchParams.get("modelName") || model;
  const trim = searchParams.get("trim");
  const drive = searchParams.get("drive");

  useEffect(() => {
    setLoading(true);
    api.post("/fitment/search", {
      year: year ? Number(year) : null,
      make, model, trim, drive,
    }).then((r) => { setResult(r.data); setLoading(false); })
      .catch(() => { setResult({ total: 0, grouped: {}, detected: {} }); setLoading(false); });
  }, [year, make, model, trim, drive]);

  const label = [year, makeName, modelName, trim].filter(Boolean).join(" ") || "Your Vehicle";
  const detected = result?.detected;

  return (
    <div className="pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.3em] text-[#00f0ff]">Fitment Results</span>
          <h1 className="font-display text-5xl md:text-7xl uppercase leading-none mt-3">Parts for <span className="bttf-text">{label}</span></h1>
        </Reveal>

        {detected && detected.used_api && (detected.bolt_patterns?.length > 0 || detected.rim_diameters?.length > 0) && (
          <div className="mt-6 flex flex-wrap gap-4" data-testid="detected-specs">
            <div className="flex items-center gap-2 border border-[#27272a] px-4 py-2.5 text-sm">
              <Gauge size={16} className="text-[#00f0ff]" />
              <span className="text-zinc-400 uppercase tracking-wider text-xs">Bolt Pattern</span>
              <span className="font-mono text-white">{detected.bolt_patterns.join(", ") || "—"}</span>
            </div>
            {detected.rim_diameters?.length > 0 && (
              <div className="flex items-center gap-2 border border-[#27272a] px-4 py-2.5 text-sm">
                <CheckCircle2 size={16} className="text-[#00f0ff]" />
                <span className="text-zinc-400 uppercase tracking-wider text-xs">OE Wheel Sizes</span>
                <span className="font-mono text-white">{detected.rim_diameters.map((d) => `${d}"`).join(", ")}</span>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 mb-14">
          <VehicleSelector variant="page" initial={{ year, make, makeName, model, modelName, trim, drive }} />
        </div>

        {loading ? (
          <div className="py-24 text-center text-zinc-500">Checking real OE fitment...</div>
        ) : result && result.total === 0 ? (
          <div className="py-24 text-center">
            <p className="text-zinc-400 mb-4">No sample parts matched this configuration.</p>
            <Link to="/category/rims" className="text-[#00f0ff] hover:underline">Browse all rims instead</Link>
          </div>
        ) : (
          <div className="space-y-16">
            {(() => {
              const rims = result?.grouped?.rims || [];
              const tires = result?.grouped?.tires || [];
              const susp = result?.grouped?.suspension || [];
              const kit = susp.find((s) => /lift|leveling/i.test(s.sub_category || "")) || susp[0];
              if (rims.length && tires.length && kit) {
                return <BuildBundle rim={rims[0]} tire={tires[0]} kit={kit} label={[makeName, modelName].filter(Boolean).join(" ") || "truck"} />;
              }
              return null;
            })()}
            {["rims", "tires", "suspension"].map((cat) => {
              const list = result?.grouped?.[cat] || [];
              if (!list.length) return null;
              return (
                <div key={cat}>
                  <div className="flex items-baseline justify-between mb-6 border-b border-[#27272a] pb-3">
                    <h2 className="font-display text-3xl md:text-4xl uppercase">{CAT_LABEL[cat]} <span className="text-[#00f0ff] text-xl">({list.length})</span></h2>
                    <Link to={`/category/${cat}`} className="text-sm uppercase tracking-widest text-zinc-400 hover:text-[#00f0ff]">Shop all {cat}</Link>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {list.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
